/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './AudioPlayer.css';
import CloseIcon from './CloseIcon';

// Interfaces
interface AudioConfig {
  speaking_rate: number;
  url: string;
}

interface PlayerConfig {
  languageCode: 'th' | 'en';
  speakingRate: number;
}

interface PlayerElementProps {
  language: string;
  speed: string;
  index: number;
  keyCode: string;
  onLanguageToggle: (index: number) => void;
  onSpeedCycle: (index: number) => void;
  onRemove: (index: number) => void;
  isSimplified: boolean;
}

const PlayerElement: React.FC<PlayerElementProps> = ({ 
  language, 
  speed, 
  index, 
  keyCode, 
  onLanguageToggle, 
  onSpeedCycle, 
  onRemove, 
  isSimplified 
}) => {
  return (
    <div className="playback-bar">
      <div className="progress-bar" />
      {isSimplified ? (
        <span className="language-button">{language}</span>
      ) : (
        <button 
          className="language-button" 
          onClick={() => onLanguageToggle(index)}
        >
          {language}
        </button>
      )}
      <button 
        className="speed-button" 
        onClick={() => onSpeedCycle(index)}
        style={{ display: language === 'EN' ? 'none' : '' }}
        title={keyCode ? `Cycle speaking rate ('${keyCode}')` : "Cycle speaking rate"}
      >
        {`${speed}x`}
      </button>
      {!isSimplified && (
        <CloseIcon 
          onClose={() => onRemove(index)} 
          closeWithKeyboard={false}
        />
      )}
    </div>
  );
}

interface ProgressState {
  isThai: boolean;
  progress: number;
  duration: number;
}

interface AudioPlayerProps {
  baseUri: string;
  audioConfigEnglish: AudioConfig[];
  audioConfigThai: AudioConfig[];
  players: PlayerConfig[];
  simplified?: boolean;
  sentenceIndex?: number; // Optional for simplified mode
  isAutoStepForward?: boolean; // Optional for simplified mode
  isThaiWordFocused?: boolean; // Optional for simplified mode
  onAudioComplete?: () => void;
  onAutoStepForwardToggle?: (value: boolean) => void;
  onUpdatePlayers?: (players: PlayerConfig[]) => void;
  onRemovePlayer?: (index: number) => void;
  onAddPlayer?: (languageCode: 'th' | 'en') => void;
  onUpdateSpeakingRate?: (rate: number, index: number) => void;
  onStartPlay?: () => void;
  onStopPlay?: () => void;
  onProgressChanged?: (state: ProgressState) => void;
  onAudioStopped?: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ 
  baseUri, 
  audioConfigEnglish, 
  audioConfigThai, 
  players,
  simplified = false,
  // Provide defaults for optional props
  sentenceIndex = 0, 
  isAutoStepForward = false,
  isThaiWordFocused = false,
  onAudioComplete = () => {},
  onAutoStepForwardToggle = () => {},
  onUpdatePlayers = () => {},
  onRemovePlayer = () => {},
  onAddPlayer = () => {},
  onUpdateSpeakingRate = () => {},
  onStartPlay = () => {},
  onStopPlay = () => {},
  onProgressChanged = () => {},
  onAudioStopped = () => {}
}) => {
  const generateAudioUrl = useCallback((player: PlayerConfig): string => {
    if (player.languageCode === 'th') {
      const config = audioConfigThai.find(config => config.speaking_rate === player.speakingRate);
      // Provide a fallback URL or handle error if config not found
      return config ? baseUri + config.url : ''; 
    } else {
      // Ensure audioConfigEnglish has at least one element
      return audioConfigEnglish.length > 0 ? baseUri + audioConfigEnglish[0].url : '';
    }
  }, [baseUri, audioConfigEnglish, audioConfigThai]);

  const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const playbackBarsRef = useRef<HTMLDivElement>(null); // Type for the container div
  const animationFrameRef = useRef<number | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => stopAndResetPlayback();
  }, []);

  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    // Check playbackBarsRef.current before accessing children
    if (audio.duration && !isNaN(audio.duration) && isPlaying && playbackBarsRef.current) {
      let progress = audio.currentTime / audio.duration;
      
      const updatePreviousPlaybackBars = () => {
        if (!playbackBarsRef.current) return;
        for (let i = 0; i < currentTrackIndex; i++) {
          // Ensure children exist and are HTMLElements
          const prevPlayerElement = playbackBarsRef.current.children[i] as HTMLElement;
          if (prevPlayerElement && prevPlayerElement.style) {
             const prevProgress = parseFloat(prevPlayerElement.style.getPropertyValue('--progress'));
             if (!isNaN(prevProgress) && prevProgress < 100) {
                prevPlayerElement.style.setProperty('--progress', '100%');
              }
          }
        }
      };

      updatePreviousPlaybackBars();
      if (progress > 0.98) progress = 1;

      // Ensure children exist and are HTMLElements
      const playerElement = playbackBarsRef.current.children[currentTrackIndex] as HTMLElement;
      if (playerElement && playerElement.style) {
          playerElement.style.setProperty('--progress', `${progress * 100}%`);
      }

      onProgressChanged({
        isThai: players[currentTrackIndex].languageCode === 'th',
        progress: progress,
        duration: audio.duration
      });

      if (!audio.ended) {
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    } else {
      // Check if still mounted before requesting frame
      if (audioRef.current) { 
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    }
  }, [isPlaying, currentTrackIndex, players, onProgressChanged]);

  // Internal state setter - type boolean
  const _setIsPlaying = (newIsPlaying: boolean) => {
    setIsPlaying(prevIsPlaying => {
      if (newIsPlaying !== prevIsPlaying) {
        if (newIsPlaying) {
          onStartPlay();
        } else {
          onStopPlay();
        }
        return newIsPlaying;
      }
      return prevIsPlaying;
    });
  };

  const playAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio.readyState >= audio.HAVE_FUTURE_DATA) { // Use constant instead of magic number
      audio.play().catch(error => console.error("Error playing audio:", error));
    } else {
      const canPlayHandler = () => {
        audio.play().catch(error => console.error("Error playing audio:", error));
        audio.removeEventListener('canplaythrough', canPlayHandler); // Use canplaythrough
      };
      audio.addEventListener('canplaythrough', canPlayHandler);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isThaiWordFocused) {
      stopAndResetPlayback(false);
      return;
    }

    const audio = audioRef.current;
    // Ensure players array is not empty and index is valid
    if (!players || players.length === 0 || !players[currentTrackIndex]) return; 
    const currentPlayer = players[currentTrackIndex];

    // Only set src if isPlaying is true
    if (isPlaying) {
      audio.autoplay = true;
      const newSrc = generateAudioUrl(currentPlayer);
      if (audio.src !== newSrc) {
        audio.src = newSrc;
      }
    } else {
      audio.autoplay = false;
      audio.src = '';
    }

    const handleEnded = () => {
      onAudioStopped();
      playNextTrack();
    };

    const handleFirstTimeUpdate = () => {
      if (isPlaying) {
        // Cancel previous frame before requesting new one
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = requestAnimationFrame(updateProgress);
        audio.removeEventListener('timeupdate', handleFirstTimeUpdate);
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleFirstTimeUpdate);

    if (isPlaying && audio.paused) {
        playAudio(); // Resume playing if isPlaying is true but audio is paused
    } else if (!isPlaying && !audio.paused) {
      audio.pause();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      onAudioStopped(); 
    }
    
    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleFirstTimeUpdate);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [
    currentTrackIndex,
    isPlaying,
    updateProgress,
    sentenceIndex, // Keep deps as identified by eslint-disable
    players,
    isThaiWordFocused,
    playAudio,
    onAudioStopped,
    generateAudioUrl
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    stopAndResetPlayback();
    if (isAutoStepForward) {
      _setIsPlaying(true);
      playAudio();
    }
  }, [sentenceIndex, playAudio, isAutoStepForward]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (simplified) {
      _setIsPlaying(true);
      playAudio();
    }
  }, [simplified, playAudio]);

  const playNextTrack = () => {
    const audio = audioRef.current; // Get audio ref here
    if (currentTrackIndex < players.length - 1) {
        setCurrentTrackIndex(prevIndex => prevIndex + 1);
        _setIsPlaying(true); 
    } else {
        // If it's the last track in the 'players' array
        if (audio) { // Ensure audio ref is current
            audio.pause(); // Explicitly pause here
            audio.currentTime = 0; // Explicitly reset time
        }
        setCurrentTrackIndex(0); // Reset to the first track
        _setIsPlaying(false);    // Stop playing state
        onAudioComplete();    // Call the onAudioComplete prop
        resetProgressBars();
    }
  };

  // Type parameter
  const stopAndResetPlayback = (shouldCallOnAudioStopped: boolean = true) => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    resetProgressBars();
    _setIsPlaying(false);
    setCurrentTrackIndex(0);
    if (shouldCallOnAudioStopped) {
      onAudioStopped(); 
    }
  };

  const togglePlayStop = () => {
    if (isPlaying) {
      stopAndResetPlayback();
    } else {
      resetProgressBars();
      _setIsPlaying(true);
      playAudio();
    }
  };

  const resetProgressBars = () => {
    if (playbackBarsRef.current) {
      const playbackBars = playbackBarsRef.current.querySelectorAll('.playback-bar') as NodeListOf<HTMLElement>; // Type NodeList
      playbackBars.forEach(bar => {
        if (bar && bar.style) {
          bar.style.setProperty('--progress', '0%');
        }
      });
    }
  };

  // Type index parameter
  const toggleLanguage = (index: number) => {
    stopAndResetPlayback();
    const newPlayers = [...players];
    // Ensure index is valid
    if (newPlayers[index]) {
      newPlayers[index].languageCode = newPlayers[index].languageCode === 'th' ? 'en' : 'th';
      newPlayers[index].speakingRate = 1.0; // Reset rate on language toggle
      onUpdatePlayers(newPlayers);
    }
  };

  // Type index parameter
  const cycleSpeed = (index: number) => {
    stopAndResetPlayback(false);

    // Ensure players and audioConfigThai exist and index is valid
    if (!players || !players[index] || !audioConfigThai || audioConfigThai.length === 0) return;

    const player = players[index];
    if (player.languageCode === 'th') {
      const speeds = audioConfigThai.map(config => config.speaking_rate).sort((a, b) => a - b);
      // Find current index robustly
      const currentSpeedIndex = speeds.findIndex(speed => speed === player.speakingRate);
      const nextSpeedIndex = currentSpeedIndex === -1 ? 0 : (currentSpeedIndex + 1) % speeds.length;
      const newSpeed = speeds[nextSpeedIndex];

      if (onUpdateSpeakingRate) {
        onUpdateSpeakingRate(newSpeed, index);
      }
    }

    _setIsPlaying(true);
    playAudio();
  };

  // Type index parameter
  const removePlayer = (index: number) => {
    onRemovePlayer(index);
    setCurrentTrackIndex(0); // Reset index after removal
    stopAndResetPlayback();
  };

  const addPlayer = () => {
    stopAndResetPlayback();
    onAddPlayer('en'); // Default to adding English player
    setCurrentTrackIndex(0); // Reset index after adding
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Using any for event type as temporary workaround for type conflict
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 's') {
        // Ensure active element blur doesn't interfere if it's part of input etc.
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        togglePlayStop();
      } else if (event.key === 'a') {
        onAutoStepForwardToggle(!isAutoStepForward);
      } else if (!simplified && players.length >= 1 && event.key >= '1' && event.key <= String(players.length)) {
        const index = parseInt(event.key, 10) - 1;
        if (players[index]?.languageCode === "th" && !isNaN(index) && index >= 0 && index < players.length) {
          cycleSpeed(index);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // Keep deps as identified by eslint-disable or previous analysis
  }, [isAutoStepForward, onAutoStepForwardToggle, togglePlayStop, players, cycleSpeed]);

  // Ensure players array is not empty before mapping
  const keyCodes = players ? Array.from({ length: players.length }, (_, i) => String(i + 1)) : [];

  return (
    <div className={`player-container ${simplified ? 'simplified' : ''}`}>
      <div className="controls-column">
        <button 
          className="play-button" 
          id="playStopButton" 
          aria-label="Play/Stop" 
          onClick={togglePlayStop}
          title={"Play/stop ('s')"}
        >
          <svg 
            className="play-icon" 
            viewBox="0 0 24 24" 
            style={{ display: isPlaying ? 'none' : 'block' }}
          >
            <path d="M8 5v14l11-7z" />
          </svg>
          <svg 
            className="stop-icon" 
            viewBox="0 0 24 24" 
            style={{ display: isPlaying ? 'block' : 'none' }}
          >
            <rect x="6" y="6" width="12" height="12" />
          </svg>
        </button>

        {!simplified && (
          <button 
            className={`auto-step-forward-button ${isAutoStepForward ? 'inverted' : ''}`} 
            aria-label="Toggle auto step-forward"
            onClick={() => onAutoStepForwardToggle(!isAutoStepForward)}
            title="Toggle auto step forward ('a')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-skip-forward">
              <polygon points="5 4 15 12 5 20 5 4"/>
              <line x1="19" x2="19" y1="5" y2="19"/>
            </svg>
          </button>
        )}
      </div>

      <div className="playback-bars-container" ref={playbackBarsRef}>
        {players?.map((player, index) => ( // Optional chaining for players map
          <PlayerElement
            key={index}
            language={
              simplified 
                ? 'TH' 
                : player.languageCode.toUpperCase()
            }
            speed={player.speakingRate.toFixed(2)}
            index={index}
            keyCode={keyCodes[index] || ''} // Provide fallback for keyCode
            onLanguageToggle={simplified ? () => {} : toggleLanguage}
            onSpeedCycle={cycleSpeed}
            onRemove={simplified ? () => {} : removePlayer}
            isSimplified={simplified}
          />
        ))}
      </div>

      {!simplified && (
        <button 
          className="add-player-button" 
          aria-label="Add new player" 
          onClick={addPlayer}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus">
            <path d="M12 5v14"/><path d="M5 12h14"/>
          </svg>
        </button>
      )}
    </div>
  );
}

export default AudioPlayer;